// ============================================================================
// mapBillingStateToJamCamEntitlements
// Pure function: maps Stripe/subscription state → JamCam billing & entitlements
// ============================================================================

import {
  BillingStateInput,
  BillingStateUpdate,
  EntitlementStatus,
  PaymentStatus,
  Plan,
  SubscriptionStatus,
} from './types';
import { resolveFeatures } from './features';

const GRACE_PERIOD_DEFAULT_DAYS = 7;

/**
 * Maps Stripe subscription state to JamCam billing state.
 *
 * This is the single source of truth for how Stripe state translates into
 * JamCam plan/entitlement/feature state. Both the webhook handler and
 * api-services use this to ensure consistency.
 */
export function mapBillingStateToJamCamEntitlements(
  input: BillingStateInput
): BillingStateUpdate {
  const {
    stripeSubscriptionStatus,
    cancelAtPeriodEnd,
    currentPeriodEnd,
    trialEnd,
    latestInvoiceStatus,
    gracePeriodDays = GRACE_PERIOD_DEFAULT_DAYS,
  } = input;

  // Default: free user with no subscription
  if (!stripeSubscriptionStatus || stripeSubscriptionStatus === 'canceled') {
    return {
      plan: 'free',
      subscriptionStatus:
        stripeSubscriptionStatus === 'canceled' ? 'canceled' : 'none',
      entitlementStatus: 'free',
      paymentStatus: 'none',
      features: resolveFeatures('free'),
      shouldBumpEntitlementVersion: true,
    };
  }

  const subscriptionStatus = mapStripeStatus(stripeSubscriptionStatus);
  const plan: Plan = subscriptionStatus === 'none' ? 'free' : 'pro';

  let entitlementStatus: EntitlementStatus;
  let paymentStatus: PaymentStatus;

  switch (subscriptionStatus) {
    case 'trialing': {
      const trialActive = trialEnd && new Date(trialEnd) > new Date();
      entitlementStatus = trialActive ? 'trial' : 'paid';
      paymentStatus = 'none';
      break;
    }

    case 'active': {
      entitlementStatus = 'paid';
      paymentStatus = 'ok';
      break;
    }

    case 'past_due': {
      // Grant grace period before blocking
      const isInGrace = isWithinGracePeriod(currentPeriodEnd, gracePeriodDays);
      entitlementStatus = isInGrace ? 'grace' : 'blocked';
      paymentStatus = 'failed';
      break;
    }

    case 'incomplete': {
      entitlementStatus = 'blocked';
      paymentStatus = latestInvoiceStatus === 'paid' ? 'ok' : 'failed';
      break;
    }

    case 'unpaid': {
      entitlementStatus = 'blocked';
      paymentStatus = 'failed';
      break;
    }

    case 'canceled': {
      entitlementStatus = 'free';
      paymentStatus = 'none';
      break;
    }

    default: {
      entitlementStatus = 'free';
      paymentStatus = 'none';
      break;
    }
  }

  return {
    plan,
    subscriptionStatus,
    entitlementStatus,
    paymentStatus,
    features: resolveFeatures(entitlementStatus),
    shouldBumpEntitlementVersion: true,
  };
}

function mapStripeStatus(stripeStatus: string): SubscriptionStatus {
  const mapping: Record<string, SubscriptionStatus> = {
    trialing: 'trialing',
    active: 'active',
    past_due: 'past_due',
    canceled: 'canceled',
    incomplete: 'incomplete',
    incomplete_expired: 'incomplete',
    unpaid: 'unpaid',
    paused: 'canceled', // treat paused as canceled for entitlement purposes
  };
  return mapping[stripeStatus] ?? 'none';
}

function isWithinGracePeriod(
  currentPeriodEnd: string | null,
  gracePeriodDays: number
): boolean {
  if (!currentPeriodEnd) return false;
  const graceEnd = new Date(currentPeriodEnd);
  graceEnd.setDate(graceEnd.getDate() + gracePeriodDays);
  return new Date() < graceEnd;
}
